import { compressImage } from "@/lib/compress-image";
import { BaseProvider } from "./base.provider";

export interface PhotoSpotListItem {
  spot_id: string;
  created_by: string;
  name: string;
  description?: string;
  lat: number;
  lng: number;
  img_obj_url?: string;
  distance_meters?: number;
  is_active: boolean;
  created_at: string;
}

export interface PhotoSpotDetail extends PhotoSpotListItem {
  creator_username: string;
  creator_avatar_obj_url?: string;
  upvote_count: number;
  downvote_count: number;
  viewer_vote?: number;
  updated_at: string;
}

export interface PaginatedPhotoSpots {
  data: PhotoSpotListItem[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface CreatePhotoSpotInput {
  name: string;
  description?: string;
  lat: number;
  lng: number;
}

export interface ImageUploadUrlResponse {
  upload_url: string;
  form_fields: Record<string, string>;
}

class PhotoSpotsProvider extends BaseProvider {
  listNear(lat: number, lng: number, radius_m?: number, page?: number): Promise<PaginatedPhotoSpots> {
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
    if (radius_m !== undefined) params.set("radius_m", String(radius_m));
    if (page !== undefined) params.set("page", String(page));
    return this.request<PaginatedPhotoSpots>(`/v1/photo-spots?${params}`);
  }

  create(input: CreatePhotoSpotInput): Promise<PhotoSpotDetail> {
    return this.request<PhotoSpotDetail>("/v1/photo-spots", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  listByUser(userId: string, page?: number): Promise<PaginatedPhotoSpots> {
    const params = new URLSearchParams();
    if (page !== undefined) params.set("page", String(page));
    const qs = params.toString();
    return this.request<PaginatedPhotoSpots>(
      `/v1/users/${encodeURIComponent(userId)}/photo-spots${qs ? `?${qs}` : ""}`,
    );
  }

  requestImageUpload(spotId: string): Promise<ImageUploadUrlResponse> {
    return this.request<ImageUploadUrlResponse>(`/v1/photo-spots/${encodeURIComponent(spotId)}/image`, {
      method: "POST",
    });
  }

  async uploadImageToStorage(
    { upload_url, form_fields }: ImageUploadUrlResponse,
    file: File,
  ): Promise<void> {
    const compressed = await compressImage(file);
    const form = new FormData();
    // S3 requires form_fields in exact server-returned order, file must be last
    for (const [key, value] of Object.entries(form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", compressed.type);
    form.append("file", compressed);
    const res = await fetch(upload_url, { method: "POST", body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Storage upload failed: HTTP ${res.status}`);
    }
  }

  getDetail(spotId: string): Promise<PhotoSpotDetail> {
    return this.request<PhotoSpotDetail>(`/v1/photo-spots/${spotId}`);
  }

  vote(spotId: string, vote: 1 | -1 | null): Promise<void> {
    return this.request<void>(`/v1/photo-spots/${spotId}/vote`, {
      method: "POST",
      body: JSON.stringify({ vote }),
    });
  }
}

export const photoSpotsProvider = new PhotoSpotsProvider();
